import React from 'react';
import { Link } from 'react-router-dom';
import { Truck, ArrowLeft, Shield, AlertTriangle, CheckCircle2, Scale, FileText, Users, CreditCard, Lock } from 'lucide-react';

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <Truck className="h-8 w-8 text-orange-500" />
            <span className="text-xl font-bold text-slate-900">True Choice Enterprise</span>
          </Link>
          <h1 className="text-3xl font-bold text-slate-900">Terms of Service</h1>
          <p className="text-slate-600 mt-2">Platform Usage Agreement for All Subscribers</p>
          <p className="text-sm text-slate-500 mt-1">Last Updated: January 2025</p>
        </div>

        {/* Agreement Content */}
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden">
          {/* Critical Disclaimer Banner */}
          <div className="bg-red-50 border-b border-red-200 p-6">
            <div className="flex gap-4">
              <AlertTriangle className="h-6 w-6 text-red-500 flex-shrink-0 mt-1" />
              <div>
                <h2 className="font-bold text-red-800 text-lg">IMPORTANT DISCLAIMER - PLEASE READ CAREFULLY</h2>
                <p className="text-red-700 mt-2 font-medium">
                  TRUE CHOICE ENTERPRISE IS A BROKER-FREE PLATFORM. USE OF TRUE CHOICE ENTERPRISE IS AT YOUR OWN RISK. 
                  True Choice Enterprise is not responsible for any transactions conducted through this platform and 
                  does not recommend or refer any particular persons or businesses. True Choice Enterprise does not 
                  assume any responsibility for financial loss or gain, property loss or gain, or any damages to any 
                  subscribers. True Choice Enterprise simply provides a platform that connects carriers with people 
                  for the purposes of moving freight, cargo, and household goods.
                </p>
              </div>
            </div>
          </div>

          <div className="p-8 space-y-8">
            {/* Section 1 - Platform Description */}
            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">1</span>
                Platform Description &amp; Purpose
              </h2>
              <div className="bg-slate-50 rounded-xl p-6 space-y-4">
                <p className="text-slate-700">
                  True Choice Enterprise (&quot;Platform,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) is a technology platform that facilitates 
                  connections between:
                </p>
                <ul className="space-y-2 text-slate-700">
                  <li className="flex gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span><strong>Customers:</strong> Individuals and businesses seeking transportation services for freight, cargo, or household goods</span>
                  </li>
                  <li className="flex gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span><strong>Carriers:</strong> Trucking companies and transportation providers offering moving services</span>
                  </li>
                  <li className="flex gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span><strong>Drivers:</strong> Commercial drivers seeking employment opportunities with carriers</span>
                  </li>
                </ul>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mt-4">
                  <p className="text-slate-700 font-medium">
                    <strong>True Choice Enterprise acts solely as an intermediary platform.</strong> We do not provide 
                    transportation services directly, do not employ drivers, and do not own or operate any commercial 
                    motor vehicles.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 2 - Assumption of Risk */}
            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">2</span>
                Assumption of Risk
              </h2>
              <div className="bg-red-50 border border-red-200 rounded-xl p-6 space-y-4">
                <p className="text-slate-700 font-medium">
                  BY USING THIS PLATFORM, YOU EXPRESSLY ACKNOWLEDGE AND AGREE THAT:
                </p>
                <ul className="space-y-3 text-slate-700">
                  <li className="flex gap-3">
                    <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <span><strong>Use At Your Own Risk:</strong> Your use of True Choice Enterprise and any transactions 
                    conducted through the Platform are entirely at your own risk.</span>
                  </li>
                  <li className="flex gap-3">
                    <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <span><strong>No Guarantees:</strong> We make no guarantees regarding the quality, safety, legality, 
                    or reliability of any services offered by carriers or drivers on the Platform.</span>
                  </li>
                  <li className="flex gap-3">
                    <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <span><strong>Independent Verification:</strong> You are solely responsible for independently verifying 
                    the credentials, insurance, licensing, and reputation of any carrier or driver before engaging their services.</span>
                  </li>
                  <li className="flex gap-3">
                    <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <span><strong>Due Diligence:</strong> You should conduct your own due diligence and obtain appropriate 
                    insurance coverage for your shipments and property.</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 3 - No Recommendations or Referrals */}
            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">3</span>
                No Recommendations or Referrals
              </h2>
              <div className="bg-slate-50 rounded-xl p-6 space-y-4">
                <p className="text-slate-700">
                  True Choice Enterprise explicitly states that:
                </p>
                <ul className="space-y-2 text-slate-700">
                  <li className="flex gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    We do NOT recommend, endorse, or refer any particular carrier, driver, person, or business
                  </li>
                  <li className="flex gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    The display of any carrier or driver on our Platform does NOT constitute an endorsement or recommendation
                  </li>
                  <li className="flex gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    Any ratings, reviews, or feedback displayed are user-generated and do NOT represent our opinion or endorsement
                  </li>
                  <li className="flex gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    Background check information provided is for informational purposes only and should not be the sole basis for any decision
                  </li>
                  <li className="flex gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    We do NOT verify the accuracy of information provided by users on the Platform
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 4 - Limitation of Liability */}
            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">4</span>
                Limitation of Liability
              </h2>
              <div className="bg-red-50 border border-red-200 rounded-xl p-6 space-y-4">
                <p className="text-slate-700 font-medium">
                  TRUE CHOICE ENTERPRISE SHALL NOT BE LIABLE FOR AND DOES NOT ASSUME ANY RESPONSIBILITY FOR:
                </p>
                <ul className="space-y-3 text-slate-700">
                  <li className="flex gap-3">
                    <Scale className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <span><strong>Financial Loss or Gain:</strong> Any financial losses, damages, costs, or expenses incurred 
                    by any subscriber in connection with transactions conducted through the Platform</span>
                  </li>
                  <li className="flex gap-3">
                    <Scale className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <span><strong>Property Loss or Gain:</strong> Any loss, damage, destruction, theft, or diminution in 
                    value of any property, goods, freight, cargo, or household items</span>
                  </li>
                  <li className="flex gap-3">
                    <Scale className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <span><strong>Personal Injury:</strong> Any personal injury, bodily harm, or death arising from or 
                    related to services obtained through the Platform</span>
                  </li>
                  <li className="flex gap-3">
                    <Scale className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <span><strong>Third-Party Actions:</strong> Any actions, omissions, negligence, fraud, or misconduct 
                    of any carrier, driver, customer, or other third party</span>
                  </li>
                  <li className="flex gap-3">
                    <Scale className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <span><strong>Service Disputes:</strong> Any disputes arising between customers and carriers or 
                    between carriers and drivers regarding services, payment, or performance</span>
                  </li>
                  <li className="flex gap-3">
                    <Scale className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <span><strong>Indirect Damages:</strong> Any indirect, incidental, special, consequential, or 
                    punitive damages of any kind</span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 5 - Transaction Disclaimer */}
            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">5</span>
                Transaction Disclaimer
              </h2>
              <div className="bg-slate-50 rounded-xl p-6 space-y-4">
                <p className="text-slate-700">
                  All transactions conducted through True Choice Enterprise are strictly between the parties involved 
                  (e.g., customer and carrier). We are not a party to any such transaction and:
                </p>
                <ul className="space-y-2 text-slate-700">
                  <li className="flex gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    Do not guarantee the completion or quality of any services
                  </li>
                  <li className="flex gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    Are not responsible for payment disputes between parties
                  </li>
                  <li className="flex gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    Do not escrow, hold, or process payments between customers and carriers (subscription fees are separate)
                  </li>
                  <li className="flex gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    Will not mediate or arbitrate disputes between users
                  </li>
                  <li className="flex gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    Are not responsible for ensuring any party fulfills their obligations
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 6 - Subscription Terms */}
            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">6</span>
                Pricing &amp; Transaction Fees
              </h2>
              <div className="bg-slate-50 rounded-xl p-6 space-y-4">
                <p className="text-slate-700">By using True Choice Enterprise, you agree to the following pricing structure:</p>
                <ul className="space-y-2 text-slate-700">
                  <li className="flex gap-2">
                    <CreditCard className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <span><strong>Platform Access:</strong> FREE - No monthly subscription fees</span>
                  </li>
                  <li className="flex gap-2">
                    <CreditCard className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                    <span><strong>Transaction Fee:</strong> $5.00 automatic fee charged to BOTH parties (customer and carrier) when a job is accepted by both parties</span>
                  </li>
                  <li className="flex gap-2">
                    <CreditCard className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                    <span><strong>Automatic Billing:</strong> Transaction fees are automatically processed when both parties accept a posted job</span>
                  </li>
                  <li className="flex gap-2">
                    <CreditCard className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <span><strong>No Refunds:</strong> Transaction fees are non-refundable once charged</span>
                  </li>
                </ul>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mt-4">
                  <p className="text-slate-700 text-sm">
                    <strong>How it works:</strong> When a customer posts a job and a carrier submits a quote, 
                    the $5.00 transaction fee is charged to both parties only after the customer accepts the 
                    quote AND the carrier confirms the job. This ensures you only pay when a real connection is made.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 7 - User Responsibilities */}
            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">7</span>
                User Responsibilities
              </h2>
              <div className="bg-slate-50 rounded-xl p-6 space-y-4">
                <p className="text-slate-700">All subscribers agree to:</p>
                <ul className="space-y-2 text-slate-700">
                  <li className="flex gap-2">
                    <Users className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                    Provide accurate and truthful information during registration and use of the Platform
                  </li>
                  <li className="flex gap-2">
                    <Users className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                    Maintain the confidentiality of their account credentials
                  </li>
                  <li className="flex gap-2">
                    <Users className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                    Use the Platform only for lawful purposes
                  </li>
                  <li className="flex gap-2">
                    <Users className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                    Not engage in fraudulent or deceptive practices
                  </li>
                  <li className="flex gap-2">
                    <Users className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                    Comply with all applicable laws and regulations
                  </li>
                  <li className="flex gap-2">
                    <Users className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
                    Obtain appropriate insurance for their activities
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 8 - Indemnification */}
            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">8</span>
                Indemnification
              </h2>
              <div className="bg-slate-50 rounded-xl p-6 space-y-4">
                <p className="text-slate-700">
                  You agree to indemnify, defend, and hold harmless True Choice Enterprise, its officers, directors, 
                  employees, agents, and affiliates from and against any and all claims, damages, losses, costs, 
                  and expenses (including reasonable attorneys&apos; fees) arising from or related to:
                </p>
                <ul className="space-y-2 text-slate-700">
                  <li className="flex gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    Your use of the Platform
                  </li>
                  <li className="flex gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    Your violation of these Terms of Service
                  </li>
                  <li className="flex gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    Your violation of any applicable law or regulation
                  </li>
                  <li className="flex gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    Any transaction or interaction with other users
                  </li>
                  <li className="flex gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    Any content or information you submit to the Platform
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 9 - Termination */}
            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">9</span>
                Account Termination
              </h2>
              <div className="bg-slate-50 rounded-xl p-6 space-y-4">
                <p className="text-slate-700">
                  True Choice Enterprise reserves the right to suspend or terminate your account at any time, 
                  with or without cause, and with or without notice. We may terminate accounts for:
                </p>
                <ul className="space-y-2 text-slate-700">
                  <li className="flex gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    Violation of these Terms of Service
                  </li>
                  <li className="flex gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    Fraudulent or illegal activity
                  </li>
                  <li className="flex gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    Providing false information
                  </li>
                  <li className="flex gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    Non-payment of subscription fees
                  </li>
                  <li className="flex gap-2">
                    <span className="text-orange-500 font-bold">•</span>
                    Any conduct deemed harmful to other users or the Platform
                  </li>
                </ul>
              </div>
            </section>

            {/* Section 10 - Governing Law */}
            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">10</span>
                Governing Law &amp; Dispute Resolution
              </h2>
              <div className="bg-slate-50 rounded-xl p-6 space-y-4">
                <p className="text-slate-700">
                  These Terms of Service shall be governed by and construed in accordance with the laws of the 
                  United States. Any disputes arising from these Terms or your use of the Platform shall be 
                  resolved through binding arbitration in accordance with the rules of the American Arbitration Association.
                </p>
                <p className="text-slate-700 mt-4">
                  <strong>Class Action Waiver:</strong> You agree to resolve any disputes on an individual basis and 
                  waive any right to participate in a class action lawsuit or class-wide arbitration.
                </p>
              </div>
            </section>

            {/* Section 11 - Acknowledgment */}
            <section>
              <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center text-sm font-bold">11</span>
                Acknowledgment &amp; Acceptance
              </h2>
              <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                <p className="text-slate-700">
                  By checking the agreement box during registration and using the True Choice Enterprise platform, 
                  I acknowledge that:
                </p>
                <ul className="space-y-2 text-slate-700 mt-4">
                  <li className="flex gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    I have read, understood, and agree to be bound by these Terms of Service
                  </li>
                  <li className="flex gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    I understand that use of this Platform is AT MY OWN RISK
                  </li>
                  <li className="flex gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    I understand that True Choice Enterprise does NOT recommend or refer any persons or businesses
                  </li>
                  <li className="flex gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    I understand that True Choice Enterprise is NOT responsible for any financial or property loss or gain
                  </li>
                  <li className="flex gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    I understand that True Choice Enterprise simply provides a platform for connections
                  </li>
                </ul>
              </div>
            </section>

            {/* Contact Information */}
            <section className="border-t border-slate-200 pt-8">
              <h2 className="text-lg font-bold text-slate-900 mb-4">Contact Information</h2>
              <p className="text-slate-600">
                If you have questions about these Terms of Service, please contact us at:
              </p>
              <div className="mt-4 bg-slate-50 rounded-xl p-4">
                <p className="text-slate-700"><strong>Email:</strong> legal@truechoiceenterprise.com</p>
                <p className="text-slate-700"><strong>Support:</strong> support@truechoiceenterprise.com</p>
              </div>
            </section>
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-8 text-center">
          <Link to="/auth" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900">
            <ArrowLeft className="h-4 w-4" /> Back to Registration
          </Link>
        </div>
      </div>
    </div>
  );
}
